import { Stack, Box, Text, Divider, Button, Group } from '@mantine/core';
import { Coding, Location } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { createReference, getReferenceString } from '@medplum/core';
import { useEffect, useCallback, useState, useMemo } from 'react';
import { AvailabilityDrawerSkeleton } from './AvailabilityDrawerSkeleton';
import { MedplumClient } from '@medplum/core';
import { AMBULATORY_ENCOUNTER_CLASS_CODING, VIRTUAL_ENCOUNTER_CLASS_CODING } from '@bariendo/lib/codings';
import { fetchBot } from '@/services/services';
import { alert } from '@/services/alert';
import { WeeklyAvailabilityFormData, DAYS_OF_WEEK, DayOfWeek, DayAvailability } from './types';
import { DayAvailabilityRow } from './DayAvailabilityRow';
import {
  convertTimeToHHMMSS,
  checkTimeRangeOverlap,
  fetchProviderAvailabilitySchedule,
  parseScheduleToFormData,
} from '../utils/availability';
import { getBrowserTimezone } from '../utils/common';
import { SharedAvailabilityFields } from './SharedAvailabilityFields';
import { validateSharedAvailabilityFields, ValidationResult } from './validateSharedAvailabilityFields';
import { AvailabilityTypeEnum, SetWeeklyAvailabilityRequest } from '@bariendo/lib/scheduling';
import { AvailabilityType } from '@bariendo/lib/scheduling';
import type { PractitionerWithRole } from '../types';

function getDefaultWeeklyAvailabilityFormData(browserTimezone: string): {
  encounterClass: Coding;
  timezone: string;
  location: null;
  days: DayAvailability[];
} {
  const weekdays: DayOfWeek[] = ['mon', 'tue', 'wed', 'thu', 'fri'];

  return {
    encounterClass: VIRTUAL_ENCOUNTER_CLASS_CODING,
    timezone: browserTimezone,
    location: null,
    days: DAYS_OF_WEEK.map((day) => ({
      dayOfWeek: day.value,
      enabled: weekdays.includes(day.value),
      timeRanges: weekdays.includes(day.value)
        ? [{ startTime: '09:00', endTime: '19:00', appointmentTypes: [] as Coding[] }]
        : [],
    })),
  };
}

function validateWeeklyAvailability(formData: WeeklyAvailabilityFormData): ValidationResult {
  // Validate shared fields first
  const sharedValidation = validateSharedAvailabilityFields({
    practitioner: formData.practitioner,
    timezone: formData.timezone,
    encounterClass: formData.encounterClass,
    location: formData.location,
  });

  if (!sharedValidation.valid) {
    return sharedValidation;
  }

  // Validate weekly-specific fields
  const enabledDays = formData.days.filter((day) => day.enabled);
  if (enabledDays.length === 0) {
    return { valid: false, error: 'At least one day must be enabled' };
  }

  for (const day of enabledDays) {
    if (day.timeRanges.length === 0) {
      return {
        valid: false,
        error: `${DAYS_OF_WEEK.find((d) => d.value === day.dayOfWeek)?.label} must have at least one time range`,
      };
    }

    for (const timeRange of day.timeRanges) {
      if (!timeRange.startTime || !timeRange.endTime) {
        return { valid: false, error: 'All time ranges must have start and end times' };
      }

      const start = convertTimeToHHMMSS(timeRange.startTime);
      const end = convertTimeToHHMMSS(timeRange.endTime);

      if (start >= end) {
        return {
          valid: false,
          error: `End time must be after start time for ${DAYS_OF_WEEK.find((d) => d.value === day.dayOfWeek)?.label}`,
        };
      }

      if (timeRange.appointmentTypes.length === 0) {
        return {
          valid: false,
          error: `Each time range must have at least one appointment type selected for ${DAYS_OF_WEEK.find((d) => d.value === day.dayOfWeek)?.label}`,
        };
      }
    }

    // Check for overlapping time ranges
    const timeRangesInHHMMSS = day.timeRanges.map((tr) => ({
      startTime: convertTimeToHHMMSS(tr.startTime),
      endTime: convertTimeToHHMMSS(tr.endTime),
      appointmentTypes: tr.appointmentTypes as Coding[],
    }));

    if (checkTimeRangeOverlap(timeRangesInHHMMSS)) {
      return {
        valid: false,
        error: `Time ranges cannot overlap on ${DAYS_OF_WEEK.find((d) => d.value === day.dayOfWeek)?.label}`,
      };
    }
  }

  return { valid: true };
}

/**
 * Saves weekly availability by calling the set-weekly-availability bot
 * @param medplum - Medplum client instance
 * @param formData - The weekly availability form data
 * @returns Promise that resolves when save is complete
 * @throws Error if validation fails or save operation fails
 */
async function saveWeeklyAvailability(medplum: MedplumClient, formData: WeeklyAvailabilityFormData): Promise<void> {
  // Validate - this is the ONLY place where validation should occur (on save, not on open)
  const validation = validateWeeklyAvailability(formData);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Transform form data to bot input format
  const botInput: SetWeeklyAvailabilityRequest = {
    practitioner: formData.practitioner!,
    encounterClass: formData.encounterClass,
    timezone: formData.timezone,
    ...(formData.location ? { location: formData.location } : {}),
    days: formData.days
      .filter((day) => day.enabled)
      .map((day) => ({
        dayOfWeek: day.dayOfWeek,
        enabled: true,
        timeRanges: day.timeRanges.map((tr) => ({
          startTime: convertTimeToHHMMSS(tr.startTime),
          endTime: convertTimeToHHMMSS(tr.endTime),
          appointmentTypes: tr.appointmentTypes,
        })),
      })),
  };

  await fetchBot(medplum, 'set-weekly-availability', botInput);
}

interface WeeklyAvailabilityFormProps {
  initialPractitioner: PractitionerWithRole | null;
  onClose: () => void;
  onSaveSuccess: (providerId: string | undefined, type: AvailabilityType) => void;
  practitioners?: PractitionerWithRole[];
}

export function WeeklyAvailabilityForm({
  initialPractitioner,
  onClose,
  onSaveSuccess,
  practitioners,
}: WeeklyAvailabilityFormProps) {
  const medplum = useMedplum();
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  // Shared fields state
  const [practitioner, setPractitioner] = useState<PractitionerWithRole | null>(initialPractitioner);
  const [encounterClass, setEncounterClass] = useState<Coding>(VIRTUAL_ENCOUNTER_CLASS_CODING);
  const [location, setLocation] = useState<Location | null>(null);
  const [timezone, setTimezone] = useState<string>(browserTimezone);

  // Form-specific state: days
  const [days, setDays] = useState<DayAvailability[]>(
    DAYS_OF_WEEK.map((day) => ({
      dayOfWeek: day.value,
      enabled: false,
      timeRanges: [],
    }))
  );

  // Loading and saving states
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const setDefaultValues = useCallback(() => {
    const defaults = getDefaultWeeklyAvailabilityFormData(browserTimezone);
    setEncounterClass(defaults.encounterClass);
    setTimezone(defaults.timezone);
    setLocation(defaults.location);
    setDays(defaults.days);
  }, [browserTimezone]);

  // Update initial practitioner when prop changes
  useEffect(() => {
    if (initialPractitioner) {
      setPractitioner(initialPractitioner);
      setIsLoading(false);
    } else {
      setIsLoading(false);
      setPractitioner(null);
    }
  }, [initialPractitioner]);

  // Load existing schedule when practitioner ID changes
  useEffect(() => {
    async function loadExistingSchedule() {
      if (!practitioner) {
        return;
      }

      setIsLoading(true);

      try {
        const practitionerRef = createReference(practitioner);
        const schedule = await fetchProviderAvailabilitySchedule(medplum, practitionerRef);

        if (!schedule) {
          setDefaultValues();
          return;
        }

        const parsedData = parseScheduleToFormData(schedule, practitionerRef, browserTimezone);
        if (!parsedData) {
          setDefaultValues();
          return;
        }

        setEncounterClass(parsedData.encounterClass);
        setTimezone(parsedData.timezone);

        const isInPerson = parsedData.encounterClass.code === AMBULATORY_ENCOUNTER_CLASS_CODING.code;
        if (isInPerson && parsedData.location) {
          const locationResource = await medplum.readReference<Location>(parsedData.location);
          setLocation(locationResource);
        } else {
          setLocation(null);
        }

        setDays(parsedData.days);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load existing schedule:', error);
        setDefaultValues();
      } finally {
        setIsLoading(false);
      }
    }

    loadExistingSchedule();
  }, [practitioner, medplum, browserTimezone, setDefaultValues]);

  const handleSave = useCallback(async () => {
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);

      const completeData: WeeklyAvailabilityFormData = {
        practitioner: practitioner ? createReference(practitioner) : null,
        encounterClass,
        timezone,
        location: location ? createReference(location) : null,
        days,
      };

      await saveWeeklyAvailability(medplum, completeData);
      if (practitioner) {
        onSaveSuccess(getReferenceString(practitioner), AvailabilityTypeEnum.WEEKLY);
      }
      onClose();
    } catch (error) {
      alert.error(`Couldn't save availability: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  }, [practitioner, encounterClass, timezone, location, days, medplum, isSaving, onClose, onSaveSuccess]);

  const toggleDayEnabled = (dayOfWeek: DayOfWeek) => {
    setDays((prevDays) =>
      prevDays.map((day) => {
        if (day.dayOfWeek === dayOfWeek) {
          const newEnabled = !day.enabled;
          return {
            ...day,
            enabled: newEnabled,
            timeRanges:
              newEnabled && day.timeRanges.length === 0
                ? [{ startTime: '09:00', endTime: '17:00', appointmentTypes: [] as Coding[] }]
                : newEnabled
                  ? day.timeRanges
                  : [],
          };
        }
        return day;
      })
    );
  };

  const addTimeRange = (dayOfWeek: DayOfWeek) => {
    setDays((prevDays) =>
      prevDays.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              timeRanges: [
                ...day.timeRanges,
                { startTime: '09:00', endTime: '17:00', appointmentTypes: [] as Coding[] },
              ],
            }
          : day
      )
    );
  };

  const removeTimeRange = (dayOfWeek: DayOfWeek, index: number) => {
    setDays((prevDays) =>
      prevDays.map((day) => {
        if (day.dayOfWeek === dayOfWeek) {
          const newTimeRanges = day.timeRanges.filter((_, i) => i !== index);
          return {
            ...day,
            timeRanges: newTimeRanges,
            enabled: newTimeRanges.length > 0 ? day.enabled : false,
          };
        }
        return day;
      })
    );
  };

  const updateTimeRange = (dayOfWeek: DayOfWeek, index: number, field: 'startTime' | 'endTime', value: string) => {
    setDays((prevDays) =>
      prevDays.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              timeRanges: day.timeRanges.map((tr, i) => (i === index ? { ...tr, [field]: value } : tr)),
            }
          : day
      )
    );
  };

  const updateAppointmentTypes = (dayOfWeek: DayOfWeek, timeRangeIndex: number, appointmentTypes: Coding[]) => {
    setDays((prevDays) =>
      prevDays.map((day) =>
        day.dayOfWeek === dayOfWeek
          ? {
              ...day,
              timeRanges: day.timeRanges.map((tr, i) => (i === timeRangeIndex ? { ...tr, appointmentTypes } : tr)),
            }
          : day
      )
    );
  };

  if (isLoading) {
    return <AvailabilityDrawerSkeleton availabilityType={AvailabilityTypeEnum.WEEKLY} />;
  }

  return (
    <Stack gap="sm">
      <SharedAvailabilityFields
        practitioner={practitioner}
        encounterClass={encounterClass}
        location={location}
        timezone={timezone}
        onPractitionerChange={setPractitioner}
        onVisitTypeChange={setEncounterClass}
        onLocationChange={setLocation}
        onTimezoneChange={setTimezone}
        practitioners={practitioners}
      />

      <Divider />

      <Box>
        <Text size="sm" fw={500} mb="md">
          Time <span style={{ color: 'red' }}>*</span>
        </Text>
        <Stack gap="md">
          {DAYS_OF_WEEK.map((dayOption) => {
            const day = days.find((d) => d.dayOfWeek === dayOption.value)!;
            return (
              <DayAvailabilityRow
                key={dayOption.value}
                day={day}
                dayLabel={dayOption.label}
                onToggleDay={() => toggleDayEnabled(day.dayOfWeek)}
                onAddTimeRange={() => addTimeRange(day.dayOfWeek)}
                onRemoveTimeRange={(index) => removeTimeRange(day.dayOfWeek, index)}
                onUpdateTimeRange={(index, field, value) => updateTimeRange(day.dayOfWeek, index, field, value)}
                onUpdateAppointmentTypes={(timeRangeIndex, appointmentTypes) =>
                  updateAppointmentTypes(day.dayOfWeek, timeRangeIndex, appointmentTypes)
                }
                providerRoleCode={practitioner?.roleCode}
              />
            );
          })}
        </Stack>
      </Box>

      <Group justify="flex-end" mt="lg">
        <Button variant="filled" size="sm" radius="xl" color="#228be6" onClick={handleSave} loading={isSaving}>
          Save
        </Button>
      </Group>
    </Stack>
  );
}
